import { string } from 'fp-ts';
import { Reservation, SimpleReservationData } from '@mews-commander/data-access';
import { AsyncAction, CommanderThunkAction, WithReservationId, ReservableServiceValue } from '@mews-commander/types';
import { lt, showSuccessAlert, TimeUnitUtils } from '@mews/framework';
import { createFetchAction } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import {
    assignReservationSlot,
    fetchCurrentEnterpriseReservationsByReservationIds,
    updateReservationResource,
} from '@mews-commander/legacy-entities/src/actions/reservations';
import { getCurrentEnterpriseService } from '@mews-commander/legacy-entities/src/selectors/services';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getSortedReservationIds } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { getServiceStartOffset } from '@mews-commander/legacy-entities/src/ducks/services';
import { RESERVATION_BARE_MINIMUM_EXTENT } from '../constants';
import { CUT_KEYS, AVAILABILITIES_FETCH } from '../actionTypes';
import { splitAvailabilitiesByOverbooking } from '../../availability/utils/resourceCategoryAvailability';

export const changeResource =
    ({
        reservationId,
        resourceId,
        updateBlockAvailability = false,
    }: {
        resourceId: string | null | undefined;
        updateBlockAvailability: boolean;
    } & WithReservationId): CommanderThunkAction<Promise<void>> =>
    async (dispatch) => {
        const updatePromise: Promise<void> = !resourceId
            ? dispatch(assignReservationSlot({ reservationId }))
            : dispatch(updateReservationResource({ reservationId, resourceId, updateBlockAvailability }));

        await updatePromise;
        await dispatch(
            fetchCurrentEnterpriseReservationsByReservationIds({
                reservationIds: [reservationId],
                extent: RESERVATION_BARE_MINIMUM_EXTENT,
            })
        );
        showSuccessAlert({ description: lt('ChangeSpaceSuccessMessage') });
    };

export const cut =
    (keyCutterId: string, reservationIds: string[], keyCount: number | undefined): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const state = getState();
        const enterpriseId = getCurrentEnterpriseId();
        const sortedReservationIds = getSortedReservationIds(state, { ids: reservationIds, enterpriseId });
        const promise = repositories.keyCutters
            .addCommand({ keyCutterId, reservationIds: sortedReservationIds, keyCount })
            .then(() => showSuccessAlert({ description: lt('KeyCutterCommandsCreated') }));
        return dispatch(createFetchAction(CUT_KEYS, promise));
    };

export const getReservationCanUseAvailability =
    (
        selectedCategoryId: string | null | undefined,
        assignedCategoryId: string | null | undefined,
        reservation: Reservation | SimpleReservationData | undefined
    ): CommanderThunkAction<AsyncAction<boolean>> =>
    async (dispatch, getState, { repositories }) => {
        const state = getState();
        const timezone = getCurrentEnterpriseIanaTimeZoneIdentifier(state);
        const service =
            reservation?.serviceId !== undefined
                ? getCurrentEnterpriseService(state, { id: reservation.serviceId })
                : undefined;

        if (
            reservation === undefined ||
            service === undefined ||
            selectedCategoryId === undefined ||
            selectedCategoryId === null ||
            selectedCategoryId === assignedCategoryId ||
            !reservation.availabilityBlockId ||
            string.isEmpty(selectedCategoryId)
        ) {
            return dispatch(createFetchAction(AVAILABILITIES_FETCH, Promise.resolve(true)));
        }

        const { availabilityBlockId, serviceId, startUtc, endUtc } = reservation;
        const {
            data: { value },
        } = service;
        const { timeUnit } = value as ReservableServiceValue;
        const startOffset: string = getServiceStartOffset(state, { id: serviceId });

        const firstTimeUnitStartUtc = TimeUnitUtils.getTimeUnitStartUtc(
            startUtc,
            timezone,
            TimeUnitUtils.timeUnitToLuxonTimePart(timeUnit)
        );
        const timePart = TimeUnitUtils.timeUnitToDateTimePart(timeUnit);
        const lastTimeUnitStartUtc = TimeUnitUtils.getLastTimeUnitStartUtcFromEndUtc(
            endUtc,
            timezone,
            startOffset,
            timePart
        );

        const promise = repositories.services
            .getAvailabilities({
                firstTimeUnitStartUtc,
                lastTimeUnitStartUtc,
                serviceId,
            })
            .then(({ availabilities }) => {
                const { blockOverbookingAvailabilities } = splitAvailabilitiesByOverbooking(
                    availabilities,
                    selectedCategoryId,
                    1,
                    availabilityBlockId
                );

                return !blockOverbookingAvailabilities.length;
            });

        return dispatch(createFetchAction(AVAILABILITIES_FETCH, promise));
    };
